import { useState, useEffect, useCallback, useRef } from 'react'
import {
  PullToRefreshProps,
  PullToRefreshState,
  PullGestureEvent,
} from '../types'

export const usePullToRefresh = ({
  onRefresh,
  refreshThreshold = 80,
  maxPullDistance = 120,
  isRefreshing: externalIsRefreshing = false,
  disabled = false,
  debug = false,
}: Omit<PullToRefreshProps, 'children'>) => {
  const [state, setState] = useState<PullToRefreshState>({
    isPulling: false,
    pullDistance: 0,
    isOverThreshold: false,
    isRefreshing: false,
    canPull: false,
  })

  const gestureRef = useRef<PullGestureEvent | null>(null)
  const rafRef = useRef<number>()
  const containerRef = useRef<HTMLDivElement>(null)

  // Check if we're at the top of the page and can start pulling
  const checkCanPull = useCallback(() => {
    if (disabled) return false

    // CRITICAL FIX: Check actual scrolling ancestors, not nested children
    const findScrollingAncestorsAtTop = () => {
      // Check window/document scroll first
      if (window.scrollY > 0) {
        if (debug) {
          console.log('🔄 Pull-to-refresh blocked: window scroll not at top', {
            windowScrollY: window.scrollY,
          })
        }
        return false
      }

      // Check document elements
      if (document.documentElement.scrollTop > 0) {
        if (debug) {
          console.log(
            '🔄 Pull-to-refresh blocked: document.documentElement scroll not at top',
            {
              scrollTop: document.documentElement.scrollTop,
            },
          )
        }
        return false
      }

      if (document.body.scrollTop > 0) {
        if (debug) {
          console.log(
            '🔄 Pull-to-refresh blocked: document.body scroll not at top',
            {
              scrollTop: document.body.scrollTop,
            },
          )
        }
        return false
      }

      // CRITICAL FIX: Check the container itself first
      if (containerRef.current) {
        const containerStyle = window.getComputedStyle(containerRef.current)
        const containerHasScrollableY =
          containerStyle.overflowY === 'auto' ||
          containerStyle.overflowY === 'scroll' ||
          containerStyle.overflow === 'auto' ||
          containerStyle.overflow === 'scroll'

        if (containerHasScrollableY && containerRef.current.scrollTop > 0) {
          if (debug) {
            console.log(
              '🔄 Pull-to-refresh blocked: container scroll not at top',
              {
                scrollTop: containerRef.current.scrollTop,
                overflow: containerStyle.overflow,
                overflowY: containerStyle.overflowY,
              },
            )
          }
          return false
        }
      }

      // Walk up the DOM tree from container to find scrolling ancestors
      let element = containerRef.current?.parentElement
      while (element && element !== document.body) {
        const computedStyle = window.getComputedStyle(element)
        const hasScrollableY =
          computedStyle.overflowY === 'auto' ||
          computedStyle.overflowY === 'scroll' ||
          computedStyle.overflow === 'auto' ||
          computedStyle.overflow === 'scroll'

        if (hasScrollableY && element.scrollTop > 0) {
          if (debug) {
            console.log(
              '🔄 Pull-to-refresh blocked: ancestor scroll not at top',
              {
                element: element.className || element.tagName || element.id,
                scrollTop: element.scrollTop,
                overflow: computedStyle.overflow,
                overflowY: computedStyle.overflowY,
              },
            )
          }
          return false
        }

        // Also check common scrolling containers by ID/class
        if (
          (element.id === 'root' ||
            element.classList.contains('scroll') ||
            element.classList.contains('scrollable')) &&
          element.scrollTop > 0
        ) {
          if (debug) {
            console.log(
              '🔄 Pull-to-refresh blocked: scrolling ancestor not at top',
              {
                element: element.className || element.tagName || element.id,
                scrollTop: element.scrollTop,
              },
            )
          }
          return false
        }

        element = element.parentElement
      }

      // CRITICAL FIX: Check all nested scrollable elements more thoroughly
      if (containerRef.current) {
        // Check all direct and nested children with scrollable overflow
        const allChildren = containerRef.current.querySelectorAll('*')
        for (const child of allChildren) {
          const childStyle = window.getComputedStyle(child)
          const hasScrollableY =
            childStyle.overflowY === 'auto' ||
            childStyle.overflowY === 'scroll' ||
            childStyle.overflow === 'auto' ||
            childStyle.overflow === 'scroll'

          if (hasScrollableY && child.scrollTop > 0) {
            if (debug) {
              console.log(
                '🔄 Pull-to-refresh blocked: nested scroll not at top',
                {
                  element: child.className || child.tagName,
                  scrollTop: child.scrollTop,
                  overflow: childStyle.overflow,
                  overflowY: childStyle.overflowY,
                },
              )
            }
            return false
          }
        }
      }

      return true
    }

    const allScrollingElementsAtTop = findScrollingAncestorsAtTop()

    if (debug) {
      if (allScrollingElementsAtTop) {
        console.log('✅ Pull-to-refresh ALLOWED: All scrolling elements at top')
      } else {
        console.log(
          '🔄 Pull-to-refresh BLOCKED: Some scrolling element not at top',
        )
      }
    }

    return allScrollingElementsAtTop
  }, [disabled, debug])

  // Handle touch start
  const handleTouchStart = useCallback(
    (e: Event) => {
      const touchEvent = e as TouchEvent
      if (!checkCanPull() || state.isRefreshing || externalIsRefreshing) return

      const touch = touchEvent.touches[0]
      gestureRef.current = {
        startY: touch.clientY,
        currentY: touch.clientY,
        deltaY: 0,
        timestamp: Date.now(),
      }

      setState((prev) => ({ ...prev, canPull: true }))

      if (debug) {
        console.log('🔄 Pull-to-refresh: Touch started at top')
      }
    },
    [checkCanPull, state.isRefreshing, externalIsRefreshing, debug],
  )

  // Handle touch move
  const handleTouchMove = useCallback(
    (e: Event) => {
      const touchEvent = e as TouchEvent
      if (!gestureRef.current || disabled) return

      const touch = touchEvent.touches[0]
      const deltaY = touch.clientY - gestureRef.current.startY

      // CRITICAL FIX: If user is scrolling up (negative deltaY), always allow scroll
      // Reset pull state and don't prevent default
      if (deltaY < 0) {
        if (state.isPulling || state.canPull) {
          setState((prev) => ({
            ...prev,
            isPulling: false,
            pullDistance: 0,
            isOverThreshold: false,
            canPull: false,
          }))
        }
        return // Allow normal scroll behavior
      }

      // If canPull is false, don't interfere with scrolling
      if (!state.canPull) {
        return
      }

      // CRITICAL FIX: Continuously check if we're still at the top of the page
      // If not at top, reset pull state and return early to allow normal scroll
      if (!checkCanPull()) {
        setState((prev) => ({
          ...prev,
          isPulling: false,
          pullDistance: 0,
          isOverThreshold: false,
          canPull: false,
        }))
        return // Allow normal scroll behavior
      }

      // Only prevent default when actively pulling down AND we're at the top
      // This ensures we don't block upward scrolling
      if (deltaY > 0 && state.canPull) {
        touchEvent.preventDefault()
      }

      gestureRef.current.currentY = touch.clientY
      gestureRef.current.deltaY = deltaY

      // Calculate pull distance with resistance (ease out)
      const resistance = 2.5
      const adjustedDistance = Math.min(deltaY / resistance, maxPullDistance)

      const isOverThreshold = adjustedDistance >= refreshThreshold
      const wasOverThreshold = state.isOverThreshold

      setState((prev) => ({
        ...prev,
        isPulling: true,
        pullDistance: adjustedDistance,
        isOverThreshold,
      }))

      // Haptic feedback when crossing threshold (if supported)
      if (isOverThreshold && !wasOverThreshold && 'vibrate' in navigator) {
        navigator.vibrate(50)
      }

      if (debug) {
        console.log(
          '🔄 Pull distance:',
          adjustedDistance,
          'Threshold:',
          refreshThreshold,
        )
      }
    },
    [
      state.canPull,
      state.isPulling,
      state.isOverThreshold,
      disabled,
      checkCanPull,
      maxPullDistance,
      refreshThreshold,
      debug,
    ],
  )

  // Handle touch end
  const handleTouchEnd = useCallback(async () => {
    if (!gestureRef.current || !state.isPulling) {
      setState((prev) => ({ ...prev, canPull: false }))
      return
    }

    const shouldRefresh =
      state.isOverThreshold && !state.isRefreshing && !externalIsRefreshing

    if (shouldRefresh) {
      setState((prev) => ({
        ...prev,
        isRefreshing: true,
        isPulling: false,
        pullDistance: refreshThreshold, // Keep indicator visible during refresh
      }))

      if (debug) {
        console.log('🔄 Pull-to-refresh: Triggering refresh')
      }

      try {
        await onRefresh()
      } catch (error) {
        // Pull-to-refresh error
      } finally {
        // Only reset state if external refreshing is not active
        // This allows the parent component to control the refresh state
        if (!externalIsRefreshing) {
          setState((prev) => ({
            ...prev,
            isRefreshing: false,
            pullDistance: 0,
            isOverThreshold: false,
            isPulling: false,
            canPull: false,
          }))
        } else {
          // If external refreshing is active, only reset internal flags but keep visual state
          setState((prev) => ({
            ...prev,
            isRefreshing: false, // Reset internal refreshing, external will handle the rest
            isPulling: false,
            canPull: false,
          }))
        }
      }
    } else {
      // Animate back to normal position
      setState((prev) => ({
        ...prev,
        isPulling: false,
        pullDistance: 0,
        isOverThreshold: false,
        canPull: false,
      }))
    }

    gestureRef.current = null
  }, [
    state.isPulling,
    state.isOverThreshold,
    state.isRefreshing,
    externalIsRefreshing,
    onRefresh,
    refreshThreshold,
    debug,
  ])

  // Handle mouse events for desktop
  const handleMouseDown = useCallback(
    (e: Event) => {
      const mouseEvent = e as MouseEvent
      if (!checkCanPull() || state.isRefreshing || externalIsRefreshing) return

      gestureRef.current = {
        startY: mouseEvent.clientY,
        currentY: mouseEvent.clientY,
        deltaY: 0,
        timestamp: Date.now(),
      }

      setState((prev) => ({ ...prev, canPull: true }))

      const handleMouseMove = (moveEvent: MouseEvent) => {
        if (!gestureRef.current || !state.canPull) return

        const deltaY = moveEvent.clientY - gestureRef.current.startY
        if (deltaY <= 0) return

        // CRITICAL FIX: Continuously check if we're still at the top of the page
        // If not at top, reset pull state and return early
        if (!checkCanPull()) {
          setState((prev) => ({
            ...prev,
            isPulling: false,
            pullDistance: 0,
            isOverThreshold: false,
            canPull: false,
          }))
          return
        }

        const resistance = 2.5
        const adjustedDistance = Math.min(deltaY / resistance, maxPullDistance)
        const isOverThreshold = adjustedDistance >= refreshThreshold

        setState((prev) => ({
          ...prev,
          isPulling: true,
          pullDistance: adjustedDistance,
          isOverThreshold,
        }))
      }

      const handleMouseUp = async () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)

        if (!gestureRef.current || !state.isPulling) {
          setState((prev) => ({ ...prev, canPull: false }))
          return
        }

        const shouldRefresh = state.isOverThreshold

        if (shouldRefresh) {
          setState((prev) => ({
            ...prev,
            isRefreshing: true,
            isPulling: false,
            pullDistance: refreshThreshold, // Keep indicator visible during refresh
          }))
          try {
            await onRefresh()
          } finally {
            // Only reset state if external refreshing is not active
            if (!externalIsRefreshing) {
              setState((prev) => ({
                ...prev,
                isRefreshing: false,
                pullDistance: 0,
                isOverThreshold: false,
                isPulling: false,
                canPull: false,
              }))
            } else {
              // If external refreshing is active, only reset internal flags but keep visual state
              setState((prev) => ({
                ...prev,
                isRefreshing: false, // Reset internal refreshing, external will handle the rest
                isPulling: false,
                canPull: false,
              }))
            }
          }
        } else {
          setState((prev) => ({
            ...prev,
            isPulling: false,
            pullDistance: 0,
            isOverThreshold: false,
            canPull: false,
          }))
        }

        gestureRef.current = null
      }

      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    },
    [
      checkCanPull,
      state.isRefreshing,
      state.canPull,
      state.isPulling,
      state.isOverThreshold,
      externalIsRefreshing,
      maxPullDistance,
      refreshThreshold,
      onRefresh,
    ],
  )

  // Set up event listeners
  useEffect(() => {
    const element = containerRef.current || document

    if (!disabled) {
      element.addEventListener(
        'touchstart',
        handleTouchStart as EventListener,
        {
          passive: false,
        },
      )
      element.addEventListener('touchmove', handleTouchMove as EventListener, {
        passive: false,
      })
      element.addEventListener('touchend', handleTouchEnd as EventListener, {
        passive: true,
      })
      element.addEventListener('mousedown', handleMouseDown as EventListener, {
        passive: true,
      })
    }

    return () => {
      element.removeEventListener(
        'touchstart',
        handleTouchStart as EventListener,
      )
      element.removeEventListener('touchmove', handleTouchMove as EventListener)
      element.removeEventListener('touchend', handleTouchEnd as EventListener)
      element.removeEventListener('mousedown', handleMouseDown as EventListener)

      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
      }
    }
  }, [
    disabled,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handleMouseDown,
  ])

  // Update external refreshing state
  useEffect(() => {
    if (externalIsRefreshing && !state.isRefreshing) {
      // External refresh started - maintain refresh indicator position
      setState((prev) => ({
        ...prev,
        isRefreshing: externalIsRefreshing,
        pullDistance: refreshThreshold,
        isPulling: false,
        canPull: false,
      }))
    } else if (
      !externalIsRefreshing &&
      state.isRefreshing &&
      state.pullDistance > 0
    ) {
      // External refresh ended - animate back to normal
      setState((prev) => ({
        ...prev,
        isRefreshing: false,
        pullDistance: 0,
        isOverThreshold: false,
        isPulling: false,
        canPull: false,
      }))
    }
  }, [
    externalIsRefreshing,
    state.isRefreshing,
    state.pullDistance,
    refreshThreshold,
  ])

  return {
    containerRef,
    ...state,
    isRefreshing: state.isRefreshing || externalIsRefreshing,
  }
}
